

import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  Header,
  Footer,
  ImageRun,
  SectionType,
  AlignmentType,
  Table,
  TableRow,
  TableCell,
  WidthType,UnderlineType,BorderStyle,ShadingType,PageOrientation,PageBreak,TabStopType,Media,LeaderType,VerticalAlign,TableLayoutType,VerticalAlignSection,PageNumber,NumberFormat 
} from "docx";
import html2canvas from "html2canvas";
import { saveAs } from "file-saver";
// "INC-WP-42-Rebaseline" -> "INC-WP-42". Sirf print hone wale naam par lagta
// hai; lookup aur rebase-detection raw device_name par hi rehte hain.
import { displayDeviceName } from "./deviceAlias";

 
const safeLoadImage = async (path) => {
  try {
    const response = await fetch(path, { mode: "cors" });
    const blob = await response.blob();
    return await blob.arrayBuffer();
  } catch (e) {
    console.warn("Image load failed:", path, e);
    return new ArrayBuffer(0);
  }
};

// ---------------------------------------------------------------------------
// Chart capture.
//
// Every .graph wrapper holds Chart.js <canvas> elements that the browser has
// already rasterised on screen. html2canvas was cloning the entire document on
// each call just to copy those bitmaps back out, which made the export
// quadratic in the number of charts. Reading the bitmaps directly and
// compositing them at the same 3x scale produces the same image: html2canvas
// does not re-render a canvas either, it drawImage()s it at the requested
// scale, which is exactly what happens below.
// ---------------------------------------------------------------------------
const CAPTURE_SCALE = 3;

const canvasToArrayBuffer = (canvas) =>
  new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Chart capture failed"));
        return;
      }
      resolve(blob.arrayBuffer());
    }, "image/png");
  });

const captureChartElement = async (el) => {
  const sources = Array.from(el.querySelectorAll("canvas"));

  // Anything that is not canvas-backed (e.g. an ApexCharts SVG) still goes
  // through html2canvas, with the options it was called with before.
  if (sources.length === 0) {
    const rendered = await html2canvas(el, {
      scale: CAPTURE_SCALE,
      useCORS: true,
      scrollY: -window.scrollY,
    });
    return {
      arrayBuffer: await canvasToArrayBuffer(rendered),
      width: rendered.width,
      height: rendered.height,
    };
  }

  const box = el.getBoundingClientRect();
  const out = document.createElement("canvas");
  out.width = Math.max(1, Math.round(box.width * CAPTURE_SCALE));
  out.height = Math.max(1, Math.round(box.height * CAPTURE_SCALE));

  const ctx = out.getContext("2d");
  ctx.fillStyle = "#ffffff"; // html2canvas' default backdrop
  ctx.fillRect(0, 0, out.width, out.height);

  // A single .graph can stack several charts (TimeSeriesChart), so each canvas
  // is drawn at its own offset within the wrapper.
  for (const source of sources) {
    if (!source.width || !source.height) continue;
    const r = source.getBoundingClientRect();
    ctx.drawImage(
      source,
      (r.left - box.left) * CAPTURE_SCALE,
      (r.top - box.top) * CAPTURE_SCALE,
      r.width * CAPTURE_SCALE,
      r.height * CAPTURE_SCALE
    );
  }

  return {
    arrayBuffer: await canvasToArrayBuffer(out),
    width: out.width,
    height: out.height,
  };
};


export const docxExport = async ({
  startDate,
  endDate,
  chartData,
  deviceProfile,
  groupDataForCharts,
  humanTime,
  withTable,
  withGraph,
  onProgress,
  signal
}) => {
  // Optional instrumentation. Both default to undefined, so any caller that
  // omits them gets exactly the previous behaviour.
  const report = (percent, stage) => {
    if (typeof onProgress === "function") onProgress({ percent, stage });
  };
  const abortIfCancelled = () => {
    if (signal && signal.aborted) {
      const err = new Error("Export cancelled");
      err.name = "AbortError";
      throw err;
    }
  };
  report(0, "Starting");
  // const formattedStartDate = startDate.toLocaleString("en-US", {
  //   year: "numeric",
  //   month: "2-digit",
  //   day: "2-digit",
  //   // hour: "numeric",
  //   // minute: "2-digit",
  //   // hour12: true,
  // });

 const currentDate = new Date();

const formattedDate = currentDate.toLocaleString("en-US", {
  year: "numeric",
  month: "long", 
  day: "2-digit",
});
 let pageIndex=0;
  // const formattedEndDate = endDate.toLocaleString("en-US", {
  //   year: "numeric",
  //   month: "2-digit",
  //   day: "2-digit",
  //   // hour: "numeric",
  //   // minute: "2-digit",
  //   // hour12: true,
  // });

function getOrdinalParts(n) {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  const suffix = s[(v - 20) % 10] || s[v] || s[0];
  return { number: n.toString(), suffix };
}

const startParts = getOrdinalParts(startDate.getDate());
const endParts = getOrdinalParts(endDate.getDate());

const startMonth = startDate.toLocaleString("en-US", { month: "long" });
const endMonth = endDate.toLocaleString("en-US", { month: "long" });


const month = endDate.toLocaleString("en-US", { month: "long" });
const year = endDate.getFullYear();
const formattedMOnthDate = `${month}, ${year}`;

// Human-readable range for the Introduction, driven by the dates the user picked.
// "July 1 to July 31, 2026" when both ends share a year, otherwise each end
// carries its own year: "December 15, 2025 to January 14, 2026".
const reportRange =
  startDate.getFullYear() === endDate.getFullYear()
    ? `${startMonth} ${startDate.getDate()} to ${endMonth} ${endDate.getDate()}, ${endDate.getFullYear()}`
    : `${startMonth} ${startDate.getDate()}, ${startDate.getFullYear()} to ${endMonth} ${endDate.getDate()}, ${endDate.getFullYear()}`;
// "July 2026" — the reporting month, taken from the selected end date.
const reportMonthYear = `${endMonth} ${endDate.getFullYear()}`;
const defaultMargins = { top: 720, bottom: 0, left: 900, right: 900 };
 


 const imagePaths=[
  "/doc/picture1.png",
  "/doc/picture2.png",
  "/doc/picture3.png",
  "/doc/picture4.png",
  "/doc/picture5.png",
  "/doc/picture6.png",
  "/doc/picture7.png",
  "/doc/picture8.png",
  "/doc/picture9.png",
  "/doc/picture10.png",
  "/doc/picture11.png",
  "/doc/picture12.png",
  "/doc/picture13.png",
 ];
 
const imageDataList = await Promise.all(imagePaths.map(safeLoadImage));
abortIfCancelled();
report(10, "Loading assets");

const [
  imageData1,
  imageData2,
  imageData3,
  imageData4,
  imageData5,
  imageData6,
  imageData7,
  imageData8,
  imageData9,
  imageData10, 
  imageData11,
  imageData12,
  imageData13,
] = imageDataList;

  const noBorders = {
    top: { style: BorderStyle.NONE },
    bottom: { style: BorderStyle.NONE },
    left: { style: BorderStyle.NONE },
    right: { style: BorderStyle.NONE },
    insideHorizontal: { style: BorderStyle.NONE },
    insideVertical: { style: BorderStyle.NONE },
  };




  // Graph ko device id se dhoondo, position se nahi. Pehle yahan poori list
  // le kar chartDivs[index] use hota tha — wo tabhi sahi hai jab har device
  // theek ek .graph render kare. Jis profile ka chart support nahi hai wo
  // "Unsupported Chart" (bina .graph) deta hai, aur tab uske aage ke saare
  // device galat graph utha laate the.
  const chartDivFor = (deviceId) =>
    document.querySelector(`.chart-wrapper > div[data-device-id="${CSS.escape(deviceId)}"] .graph`);
  const pageBreak = new Paragraph({
    children: [new PageBreak()],
  });

  const sections = [];

  




  const sharedHeader = new Header({
    children: [
  
  new Table({
      width: {
    size: 100,
    type: WidthType.PERCENTAGE,
  },
  borders: noBorders,
  rows: [
    new TableRow({
      children: [
    
        new TableCell({
          children: [
  new Paragraph({
   spacing: {
    before: 200,
    after: 0,
    line: 280,  
  },
    children: [
      new TextRun({
        text: "Geotechnical Engineering/Instrumentation; Tunnel Design; Environmental Monitoring & Mitigation ",
        italics: true,
        size: 24,
  font: "Monotype Corsiva",
        color: "000000",  
      }),
    ],
    alignment: AlignmentType.LEFT,
  }),
 
  new Paragraph({
  spacing: {
    before: 80,
    after: 0,
    line: 80,
  },
    children: [
      new ImageRun({
        type: "png",
        data: imageData11,
        transformation: {
          width: 640,
          height:3,
        },
      }),
    ],
    alignment: AlignmentType.LEFT,
  }),
]
, 
          width: {
            size: 80,  
            type: WidthType.PERCENTAGE,
          },
      borders: noBorders,
        }),

  
        new TableCell({
          children: [
            new Paragraph({
                 indent: {
             left: -20, 
             },
              children: [
                new ImageRun({
                  type: "png",
                  data: imageData5,
                  transformation: {
                    width: 65,
                    height: 70,
                  },
                }),
              ],
              alignment: AlignmentType.LEFT,
            }), 
          ],
          width: {
            size: 10, 
            type: WidthType.PERCENTAGE,
          },
    borders: noBorders,
          margins: {
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
          },
        }),
      ],
    }),
  ],

}),


    ] 
  });
  
  
// const sharedHeader = new Header({
//   children: [
//     new Table({
//       layout: TableLayoutType.FIXED,  
//       width: { size: 100, type: WidthType.PERCENTAGE }, 
//       borders: noBorders,
//       rows: [
//         new TableRow({
//           borders: noBorders,
//           children: [
//             new TableCell({
//               borders: noBorders,
//                         width: {
//             size: 80,  
//             type: WidthType.PERCENTAGE,
//           },
//               children: [
//                 new Paragraph({
//                   alignment: AlignmentType.LEFT,
//                   children: [
//                     new TextRun({
//                       text: "Geotechnical Engineering/Instrumentation; Tunnel Design",
//                       italics: true,
//                       size: 21,
//                       color: "000000",
//                     }),
//                   ],
//                 }),
//               ],
//             }),
//             new TableCell({
//               borders: noBorders,
//                         width: {
//             size: 80,  
//             type: WidthType.PERCENTAGE,
//           },
//               children: [
//                 new Paragraph({
//                   alignment: AlignmentType.RIGHT,
//                   children: [
//                     new ImageRun({
//                       data: imageData5,
//                       transformation: { width: 60, height: 60 },
//                     }),
//                   ],
//                 }),
//               ],
//             }),
//           ],
//         }),
//       ],
//     }),
//   ],
// });
  const createFooter = (pageNum)=>{
    return new Footer({
    children: [
       new Paragraph({
  spacing: {
    before: 0,
    after: 40,
 
  },
    children: [
      new ImageRun({
        type: "png",
        data: imageData11,
        transformation: {
          width: 680,
          height: 1,
        },
      }),
    ],
    alignment: AlignmentType.LEFT,
  }),

      new Table({
        width: {
          size: 100,
          type: WidthType.PERCENTAGE,
        },
        borders:noBorders,
        rows: [
          new TableRow({
            children: [
             
              new TableCell({
                children: [
                  new Paragraph({
                    children: [
                      new TextRun({ text: "150 Executive Park Blvd., Ste.3600", italics: true,  color: "000000",  size:23,font: "Monotype Corsiva", }),
                    ],
                  }),
                   new Paragraph({
                    children: [
                      new TextRun({ text: "San Francisco, CA 94134", italics: true,  color: "000000",size:23,font: "Monotype Corsiva", }),
                    ],
                  }),
                ],
                width: { size: 33, type: WidthType.PERCENTAGE },
              }),
             
              new TableCell({
                children: [
                  new Paragraph({
                    children: [
                     new TextRun({children: ["Page ", PageNumber.CURRENT], italics: true, size:23,font: "Monotype Corsiva" }),                     ],
                      alignment: AlignmentType.CENTER,
                  }),
    new Paragraph({
 
  children: [
    new ImageRun({
      type: "png",
      data: imageData10,
      transformation: {
        width: 10,
        height: 10,
      },
    }),
    new TextRun({
      text: " www.ascengineering.org",italics: true ,
    style: "Hyperlink",
 size:23,
      color: "#002AFF",
font: "Monotype Corsiva"
    }),
  ],
  alignment: AlignmentType.CENTER,
}),

                ],
                width: { size: 34, type: WidthType.PERCENTAGE },
              }),
             
               new TableCell({
                children: [

                  new Paragraph({
  children: [
    new ImageRun({
      type: "png",
      data: imageData8,
      transformation: {
        width: 14,
        height: 14,
      },
    }),
    new TextRun({
      text: " (510) 207-1387",italics: true ,
 size:23,
      font: "Monotype Corsiva",
      color: "000000",
    }),
  ],
  alignment: AlignmentType.RIGHT,
}),
                  new Paragraph({
  children: [
    new ImageRun({
      type: "png",
      data: imageData9,
      transformation: {
        width: 14,
        height: 14,
      },
    }),
    new TextRun({
      text: " (510) 291 9733",italics: true,
  size:23,
        font: "Monotype Corsiva", 
      color: "000000",
    }),
  ],
  alignment: AlignmentType.RIGHT,
}),

                ],
                width: { size: 33, type: WidthType.PERCENTAGE },
                margins: {
                top: 0,
                  bottom: 0,
                  left: 0,
                  right: 0,
                },
              }),
            ],
          }),
        ],
      }),
    ],
  });
};

  // 12pt throughout the transmittal letter. Without an explicit size a run
  // falls back to docx's 10pt default, which is what Word was showing.
  const LETTER_SIZE = 24;

  const letterContent =[

    new Paragraph({
      spacing: { before: 500, after: 200 },
      children: [new TextRun({ text: formattedDate, size: LETTER_SIZE })],
    }),

    new Paragraph({
      children: [
        new TextRun({ text: "KSTJV", bold: true, size: LETTER_SIZE }),
        new TextRun({ text: "De La Cruz Blvd., 300,", bold: true, size: LETTER_SIZE, break: 1 }),
        new TextRun({ text: "Santa Clara, CA 95050", bold: true, size: LETTER_SIZE, break: 1 }),
      ],
      spacing: { after: 200 },
    }),
    new Paragraph({
      children: [
        new TextRun({ text: "Attention: Aaron Graser", size: LETTER_SIZE }),
      ],
      spacing: { after: 250 },
    }),
    // Hanging indent: the first line keeps the "Re:" flush left, the second
    // line starts under "VTA’s" instead of under "Re:".
    new Paragraph({
      // "Re: " measures ~21pt in 12pt Times New Roman, so a 420-twip hanging
      // indent drops the second line exactly under "VTA’s".
      indent: { left: 420, hanging: 420 },
      children: [
        new TextRun({ text: "Re: VTA’s BART Phase II, Contract CP2;", size: LETTER_SIZE }),
        new TextRun({ text: `Instrumentation & Monitoring Report – ${reportMonthYear}`, size: LETTER_SIZE, break: 1 }),
      ],
      spacing: { after: 250 },
    }),
    new Paragraph({
      spacing: { after: 240 },
      children: [
        new TextRun({ text: "Dear Aaron,", bold: true, size: LETTER_SIZE }),
      ],
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `This Letter transmits our Instrumentation & Monitoring Report for ${reportMonthYear}, for the CP2 Project. Piezometer and inclinometer data was obtained from `,
          size: LETTER_SIZE,
        }),
        new TextRun({ text: `${startMonth} `, size: LETTER_SIZE }),
        new TextRun({ text: startParts.number, size: LETTER_SIZE }),
        new TextRun({ text: startParts.suffix, size: LETTER_SIZE, superscript: true }),
        new TextRun({ text: ` to ${endMonth} `, size: LETTER_SIZE }),
        new TextRun({ text: endParts.number, size: LETTER_SIZE }),
        new TextRun({ text: endParts.suffix, size: LETTER_SIZE, superscript: true }),
        new TextRun({ text: ".", size: LETTER_SIZE }),
      ],
      spacing: { after: 400 },
    }),
    new Paragraph({
      spacing: { before: 200, after: 600 },
      children: [
        new TextRun({ text: "Please call with any questions.", size: LETTER_SIZE }),
      ],
    }),
    new Paragraph({
      spacing: { before: 200, after: 600 },
      children: [new TextRun({ text: "", size: LETTER_SIZE })],
    }),

    new Paragraph({
      spacing: { before: 600 },
      alignment: AlignmentType.CENTER,
      children: [new TextRun({ text: "Best regards,", size: LETTER_SIZE })],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      children: [
        new TextRun({ text: "Analysis & Solutions Consultants (ASC)", size: LETTER_SIZE }),
      ],
    }),

    new Paragraph({
      spacing: { before: 100, after: 100 },
      alignment: AlignmentType.CENTER,
      children: [
        new ImageRun({
          type: "png",
          data: imageData4,
          transformation: {
            width: 100,
            height: 30,
          },
        }),
      ],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      children: [new TextRun({ text: "Dots Oyenuga, Ph.D.,", size: LETTER_SIZE })],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      children: [new TextRun({ text: "P.E. Principal-in-charge", size: LETTER_SIZE })],
    }),

  ];



 
// TOC ke page number kahan tak jayenge. Letter page 12240 dxa chaudi hai;
// usme se left+right margin nikalne par content width bachti hai (10440).
// Usme se thoda aur andar, taaki number bilkul kinare se chipke nahi.
//
// TOC_RIGHT_INSET dxa me hai (1 inch = 1440). Number ko aur andar chahiye to
// ise badha do, kinare ke paas chahiye to ghata do; 0 karne par exact right
// margin par lagega.
const TOC_RIGHT_INSET = 360; // 0.25 inch
const TOC_TAB_STOP = 12240 - defaultMargins.left - defaultMargins.right - TOC_RIGHT_INSET;

// Entries "Table of Contents" heading ke "a" ke neeche se shuru hon, yaani
// heading se ek akshar (bold 14pt "T") jitna andar. 1 pt = 20 dxa, to 14pt
// Times bold ka "T" ~170 dxa banta hai.
//
// Zyada/kam andar chahiye to bas is number ko badal do; tab stop left margin
// se naapa jaata hai, indent se hilta nahi, isliye page number apni jagah
// right par hi rahenge.
const TOC_LEFT_INDENT = 200;

const tocEntry = (label, pageNum) =>
  new Paragraph({
    spacing: { before: 0, after: 120 },
    indent: { left: TOC_LEFT_INDENT },
    tabStops: [
      {
        // Label left me, page number right me, aur beech ki khaali jagah
        // barabar dots se bharti hai.
        type: TabStopType.RIGHT,
        position: TOC_TAB_STOP,
        // DOT (singular) hi valid hai. Pehle LeaderType.DOTS likha tha jo
        // undefined hota hai, to leader attribute XML me jaata hi nahi tha
        // aur Word koi dots draw nahi karta tha — isi liye labels ke andar
        // dots haath se likhne pade the.
        leader: LeaderType.DOT,
      },
    ],
    children: [
      new TextRun({ text: label, bold: true }),
      new TextRun({ text: "	", bold: true }),
      new TextRun({ text: pageNum.toString(), bold: true }),
    ],
  });

// Label me dots nahi likhne — tab stop ka dot leader khud utne dots bharta hai
// jitni jagah label aur page number ke beech bachti hai. Haath se likhe dots
// har line me alag lambai ke hote the, isliye numbers kabhi line me nahi aate.
const tableofContent = [
  tocEntry("Introduction", 4),
  tocEntry("Summary of Construction Activities", 4),
  tocEntry("Inclinometer Graphs", 5),
  tocEntry("Vibrating-Wire Piezometer Multi-Sensor Graphs", 56),
  tocEntry("Vibrating-Wire Piezometer Graphs", 61),
  tocEntry("Instruments on Alert", 83),
  tocEntry("Maintenance Activities", 83),
  tocEntry("Appendix", 84),
];

     const letterContent2 =[
  
    new Paragraph({
      spacing: { before: 0, after: 120 },
      children: [  
        new TextRun({ text: "Introduction", bold: true,break:1 }),
      ],
    }),

 
    new Paragraph({
      spacing: { after: 240, line: 340 },
      children: [
        new TextRun({ text: `This monthly Report presents piezometer and inclinometer data obtained for Early Works Package 3C, from ${reportRange}.` }),
      ],
    }),
    new Paragraph({
      spacing: { after: 240, line: 340 },
      children: [
        new TextRun({ text: "Data is collected at scheduled intervals and analyzed to identify trends, compare against design predictions, and evaluate whether any trigger levels have been approached or exceeded." }),
      ],
    }),
    new Paragraph({
      spacing: { after: 240, line: 340 },
      children: [
        new TextRun({ text: "This report includes:" }),
      ],
    }),
    new Paragraph({
      bullet: { level: 0 },
      spacing: { after: 160, line: 320 },
      children: [
        new TextRun({ text: "A summary of current instrumentation data." }),
      ],
    }),
    new Paragraph({
      bullet: { level: 0 },
      spacing: { after: 160, line: 320 },
      children: [
        new TextRun({ text: "Graphical presentation for all instruments." }),
      ],
    }),
    new Paragraph({
      bullet: { level: 0 },
      spacing: { after: 160, line: 320 },
      children: [
        new TextRun({ text: "Multi-sensor graphs for Piezometers." }),
      ],
    }),
    new Paragraph({
      bullet: { level: 0 },
      spacing: { after: 160, line: 320 },
      children: [
        new TextRun({ text: "Maintenance summary." }),
      ],
    }),

 
  ];


 
 

// const imageParagraph1 = new Paragraph({
//   spacing: { after: 500 },
//   alignment: AlignmentType.LEFT,
//   children: [
//     new ImageRun({
//       data: imageData1,
//       transformation: {
//         width: 130,
//         height: 140,
//       },
//     }),
//   ],
// });
 

// 1 page
 
// const submittedTable = new Table({
//   width: {
//     size: 100,
//     type: WidthType.PERCENTAGE,
//   },
//   borders: {
//     top: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
//     bottom: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
//     left: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
//     right: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
//     insideHorizontal: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
//     insideVertical: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
//   },
//   rows: [
//     new TableRow({
//       children: [
  
//         new TableCell({
// //           borders: {
// //   top: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
// //   bottom: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
// //   left: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
// //   right: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
// //   insideHorizontal: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
// //   insideVertical: { style: BorderStyle.SINGLE, size: 4, color: "000000" },
// // },

//           children: [
//         new Paragraph({
//   alignment: AlignmentType.CENTER,
//   border: {
//     top: { style: BorderStyle.SINGLE, size: 4, color: "000000" },
//     bottom: { style: BorderStyle.SINGLE, size: 4, color: "000000" },
//     left: { style: BorderStyle.SINGLE, size: 4, color: "000000" },
//     right: { style: BorderStyle.SINGLE, size: 4, color: "000000" },
//     insideHorizontal: { style: BorderStyle.SINGLE, size: 4, color: "000000" },
//     insideHorizontal: { style: BorderStyle.SINGLE, size: 4, color: "000000" }
//   },
//   children: [
//     new TextRun({ text: "Submitted to:", bold: true, size: 28,break:1}),
//     new TextRun({ text: "KSTJV\n", bold: true, size: 28,break:1 }),
//     new TextRun({ text: "De La Cruz Blvd., 300,\n", bold: true, size: 28,break:1 }),
//     new TextRun({ text: "Santa Clara, CA 95050", bold: true, size: 28, }),
//   ],
// }),
//           ],
//         }),
//       ],
//     }),
//   ],
// });

const submittedTable = new Table({
  width: {
    size: 100,
    type: WidthType.PERCENTAGE,
  },
  // Outer borders declared at table level too — a table whose `borders` object
  // only names the inside edges leaves the outside undefined, which is why the
  // left cell was rendering without a frame.
  borders: {
    top: { style: BorderStyle.SINGLE, size: 4, color: "000000" },
    bottom: { style: BorderStyle.SINGLE, size: 4, color: "000000" },
    left: { style: BorderStyle.SINGLE, size: 4, color: "000000" },
    right: { style: BorderStyle.SINGLE, size: 4, color: "000000" },
    insideVertical: { style: BorderStyle.SINGLE, size: 4, color: "000000" },
    insideHorizontal: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
  },
  rows: [
    new TableRow({
      children: [
        // LEFT CELL — "Submitted by:" + ASC logo
        new TableCell({
          width: { size: 62, type: WidthType.PERCENTAGE },
          borders: {
            top: { style: BorderStyle.SINGLE, size: 4, color: "000000" },
            bottom: { style: BorderStyle.SINGLE, size: 4, color: "000000" },
            left: { style: BorderStyle.SINGLE, size: 4, color: "000000" },
            right: { style: BorderStyle.SINGLE, size: 4, color: "000000" },
          },
          margins: { top: 80, bottom: 80, left: 120, right: 120 },
          children: [
            new Paragraph({
              children: [
                new TextRun({ text: "Submitted by:", bold: true, size: 24 }),
              ],
            }),
            new Paragraph({
              children: [
                new ImageRun({
                  type: "png",
                  data: imageData2,
                  transformation: { width: 130, height: 140 },
                }),
              ],
            }),
          ],
        }),

        // RIGHT CELL — "Submitted to:" address block
        new TableCell({
          width: { size: 38, type: WidthType.PERCENTAGE },
          borders: {
            top: { style: BorderStyle.SINGLE, size: 4, color: "000000" },
            bottom: { style: BorderStyle.SINGLE, size: 4, color: "000000" },
            left: { style: BorderStyle.SINGLE, size: 4, color: "000000" },
            right: { style: BorderStyle.SINGLE, size: 4, color: "000000" },
          },
          margins: { top: 200, bottom: 120, left: 120, right: 120 },
          children: [
            // docx ignores "\n" inside a run — `break: 1` is what starts a new
            // line, so the text carries no escapes of its own.
            new Paragraph({
              alignment: AlignmentType.LEFT,
              spacing: { before: 0, after: 200 },
              children: [
                new TextRun({ text: "Submitted to:", bold: true, size: 24 }),
              ],
            }),
            new Paragraph({
              alignment: AlignmentType.LEFT,
              spacing: { before: 0 },
              children: [
                new TextRun({ text: "KSTJV", bold: true, size: 24 }),
                new TextRun({ text: "De La Cruz Blvd., 300,", bold: true, size: 24, break: 1 }),
                new TextRun({ text: "Santa Clara, CA 95050", bold: true, size: 24, break: 1 }),
              ],
            }),
          ],
        }),
      ],
    }),
  ],
});


sections.push({
     properties: {
    type: SectionType.NEXT_PAGE,
    page: {
      margin: defaultMargins,
    },
  },
 // footers: { default: createFooter(1) },
  children: [
    new Paragraph({
      spacing: { after: 100 },
      alignment: AlignmentType.CENTER,
      children: [
        new TextRun({ text: "VTA\u2019s BART Silicon Valley Phase II Extension", bold: true, size: 32 }),  
      ],
    }),
    new Paragraph({
      spacing: { after: 100 },
      alignment: AlignmentType.CENTER,
      children: [
        new TextRun({ text: "Tunnel and Trackwork Project (CP2 - EWP 3C)", bold: true, size: 32 }),  
      ],
    }),

 new Paragraph({
  spacing: { after: 800 },
  alignment: AlignmentType.CENTER,
  children: [
    new TextRun({ text: "Geotechnical Instrumentation & Monitoring Report", size: 28,  underline: { type: UnderlineType.SINGLE },bold: true,  }),  
  ],
}),
     new Paragraph({
  spacing: { before: 400 },
  alignment: AlignmentType.CENTER,
  children: [
    new ImageRun({
      type: "png",
      data: imageData1,
      transformation: {
        width: 600,
        height: 400,
      },
    }),
  ],
}),
    new Paragraph({
      spacing: { before: 400, after: 800 },
      alignment: AlignmentType.CENTER,
      children: [
        new TextRun({ text: formattedMOnthDate, bold: true, size: 32 }),
      ],
    }),
   submittedTable,
new Paragraph({
  spacing: { before: 1000 },
  alignment: AlignmentType.CENTER,
  children: [
    new ImageRun({
      type: "png",
      data: imageData3,
      transformation: {
        width: 680,
        height: 10,
      },
    }),
  ],
}),
    pageBreak,
  ],  
});
// 2 page 


sections.push({
     properties: {
    type: SectionType.NEXT_PAGE,
    page: {
      margin: defaultMargins,
   pageNumbers: {
                        start: 2,
                        formatType: NumberFormat.DECIMAL,
                    },
    },
  },
  headers: { default: sharedHeader },
  footers: { default: createFooter(2)},
  children: [
      ...letterContent,
      new Paragraph({ children: [new TextRun("")] }),
      pageBreak,
  ],
});


// 3 page

sections.push({
     properties: {
    type: SectionType.NEXT_PAGE,
    page: {
      margin: defaultMargins,
    },
  },
  headers: { default: sharedHeader },
  footers: { default: createFooter(3)},
  children: [
        new Paragraph({       spacing: { 
      
        after: 200, 
       }, children: [    new TextRun({ text: "Table of Contents ", bold: true,break:1,size:28,color:'0f4761' }),] }),

      ...tableofContent,
      new Paragraph({ children: [new TextRun("")] }),
      pageBreak,
  ],
});

sections.push({
     properties: {
    type: SectionType.NEXT_PAGE,
    page: {
      margin: defaultMargins,
    },
  },
  headers: { default: sharedHeader },
  footers: { default: createFooter(4)},
  children: [
      ...letterContent2,
      new Paragraph({ children: [new TextRun("")] }),
      pageBreak,
  ],
});






const rightTextParagraphs = [
    new Paragraph({
    alignment: AlignmentType.LEFT,
    children: [new TextRun({ text: "Submitted to:", bold: true, size: 28 })],
  }),
  new Paragraph({
    alignment: AlignmentType.LEFT,
    children: [new TextRun({ text: "KSTJV", bold: true, size: 28 })],
  }),
  new Paragraph({
    alignment: AlignmentType.LEFT,  
    children: [new TextRun({ text: "De La Cruz Blvd., 300,",bold: true, size: 28 })],
  }),
  new Paragraph({
    alignment: AlignmentType.LEFT, 
    children: [new TextRun({ text: "Santa Clara, CA 95050",bold: true, size: 28 })],
  }),
];


 


  // const filteredDevices = devices.filter(
  //   (row) =>
  //     (deviceProfile === "All" || !deviceProfile || row.device_profile === deviceProfile || row.device_profile.startsWith(deviceProfile)) &&
  //     (selectedDevice === "All" || row.id == selectedDevice)
  // );
  console.log(chartData);
  let index=0;
  const totalDevices = Object.keys(chartData || {}).length || 1;

  // Multi-sensor device wo hain jinka profile "Sensor" se shuru hota hai
  // (abhi sab "SensorA" profile par hain: SensorA/B/B1/C/D). Device naam se
  // sort hone ki wajah se ye paanch ek saath aate hain, isliye section heading
  // sirf pehle wale ke page par lagti hai — baaki par dobara nahi.
  const isMultiSensor = (row) => Boolean(row?.device_profile?.startsWith("Sensor"));
  let multiSensorHeadingDone = false;

  // Inclinometer ("IS" profile) hisse par bhi wahi tarika: section heading sirf
  // uske pehle page par.
  //
  // Rebase kiya hua device (INC-WP-42-Rebaseline waale) DB me alag device hai
  // aur display par apne base naam se aata hai. Uske page par heading nahi
  // chahiye, isliye heading pehle non-rebase inclinometer par lagti hai.
  let vwpHeadingDone = false;

  const isInclinometer = (row) => Boolean(row?.device_profile?.startsWith("IS"));
  const isRebaseDevice = (row) =>
    /[\s_-]*rebase(?:line)?\s*$/i.test(String(row?.device_name ?? ""));
  let inclinometerHeadingDone = false;

  // "SensorB1" -> "Multi-Sensor Graphs Sensor B1". Profile ka prefix hata kar
  // baaki hissa waise ka waisa rakha jaata hai.
  const multiSensorLabel = (deviceName) =>
    `Multi-Sensor Graphs Sensor ${String(deviceName ?? "").replace(/^sensor[\s_-]*/i, "")}`.trim();

for (const [deviceId, row] of Object.entries(chartData)) {
if (deviceId === "22a68aa0-2ce8-11f0-b7ab-6d119f1d4014") {
    continue;
  }

    const sharedFooter1=createFooter(index+5);
    pageIndex=index+5;
    const rows = [];
    console.log(row);
    if (row.device_profile.startsWith("IS") && withTable) {
      rows.push(
        new TableRow({
          children: [
            new TableCell({
              children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: displayDeviceName(row.device_name), bold: true })] })],
           columnSpan: 3,
            }),
          ],
        })
      );

      row.data.forEach((dataRow) => {
        dataRow.forEach((item) => {
          if (item[1]) {
            rows.push(
              new TableRow({
                children: [
                  new TableCell({ children: [new Paragraph({ text: `${item[2]} key`, alignment: AlignmentType.CENTER })] }),
                  new TableCell({ children: [new Paragraph({ text: `${parseFloat(item[1]).toFixed(5)}`, alignment: AlignmentType.CENTER })] }),
                  new TableCell({ children: [new Paragraph({ text: humanTime(item[0]), alignment: AlignmentType.CENTER })] }),
                ],
              })
            );
          }
        });
      });
    }
// console.log(withTable);
if ((row.device_profile.startsWith("piezo") || row.device_profile.startsWith("sg")) && withTable) {
  // console.log(row);
      const record = groupDataForCharts(row.data);
    Object.entries(record).forEach(([_, recordItem]) => {
let type=recordItem.type[0].type;
let romoveunder=type.replace(/_/g,' ').slice(0,-2).toUpperCase();
        rows.push(
          new TableRow({
            children: [
              new TableCell({
                children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: displayDeviceName(row.device_name)+' ('+romoveunder+') ', bold: true })] })],
                columnSpan: 6,
              }),
            ],
          })
        );
        const headRow = [
          new TableCell({ children: [new Paragraph({ text: "Date", alignment: AlignmentType.CENTER })] }),
        ];
        recordItem.type.forEach((col) => {
          headRow.push(
            new TableCell({ children: [new Paragraph({ text: col.type.slice(-2), alignment: AlignmentType.CENTER })] })
          );
        });
        rows.push(new TableRow({ children: headRow }));
        recordItem.data.forEach((item) => {
          const cells = [
            new TableCell({ children: [new Paragraph({ text: humanTime(item.date), alignment: AlignmentType.CENTER })] }),
          ];
          recordItem.type.forEach((col) => {
            cells.push(
              new TableCell({ children: [new Paragraph({ text: `${item[col.type]}`, alignment: AlignmentType.CENTER })] })
            );
          });
          rows.push(new TableRow({ children: cells }));
        });
      });
    }
const waterLevelMap = {
  "VWP-WP-01": [
    "Water Level elevation (A) Sensor Elevation = +31.0 (Alarm EL. 61)",
    "Water Level elevation (B1) Sensor Elevation = -17.0 (Alarm EL. 59)",
    "Water Level elevation (B) Sensor Elevation = -28.5 (Alarm EL. 59)",
    "Water Level elevation (C) Sensor Elevation = -63.0 (Alarm EL. 59)",
    "Water Level elevation (D) Sensor Elevation = -80.0 (Alarm EL. 61)",
  ],
  "VWP-WP-02": [
    "Water Level elevation (A) Sensor Elevation = +31.0 (Alarm EL. 61)",
    "Water Level elevation (B) Sensor Elevation = -28.5 (Alarm EL. 59)",
    "Water Level elevation (C) Sensor Elevation = -63.0 (Alarm EL. 59)",
    "Water Level elevation (D) Sensor Elevation = -77.0 (Alarm EL. 61)",
  ],
  "VWP-WP-03": [
    "Water Level elevation (A) Sensor Elevation = +31.0 (Alarm EL. 61)",
    "Water Level elevation (B1) Sensor Elevation = -17.0 (Alarm EL. 59)",
    "Water Level elevation (B) Sensor Elevation = -28.5 (Alarm EL. 59)",
    "Water Level elevation (C) Sensor Elevation = -63.0 (Alarm EL. 59)",
    "Water Level elevation (D) Sensor Elevation = -80.0 (Alarm EL. 61)",
  ],
  "VWP-WP-04": [
    "Water Level elevation (A) Sensor Elevation = +31.0 (Alarm EL. 61)",
    "Water Level elevation (B1) Sensor Elevation = -17.0 (Alarm EL. 59)",
    "Water Level elevation (B) Sensor Elevation = -28.5 (Alarm EL. 59)",
    "Water Level elevation (C) Sensor Elevation = -63.0 (Alarm EL. 59)",
  ],
  "VWP-WP-05": [
    "Water Level elevation (A) Sensor Elevation = +31.0 (Alarm EL. 61)",
    "Water Level elevation (B1) Sensor Elevation = -17.0 (Alarm EL. 59)",
    "Water Level elevation (B) Sensor Elevation = -28.5 (Alarm EL. 59)",
    "Water Level elevation (C) Sensor Elevation = -63.0 (Alarm EL. 59)",
  ],
  "VWP-WP-06": [
    "Water Level elevation (A) Sensor Elevation = +31.0 (Alarm EL. 61)",
    "Water Level elevation (B) Sensor Elevation = -28.5 (Alarm EL. 59)",
    "Water Level elevation (C) Sensor Elevation = -63.0 (Alarm EL. 59)",
    "Water Level elevation (D) Sensor Elevation = -77.0 (Alarm EL. 61)",
  ],
  "VWP-WP-07": [
    "Water Level elevation (A) Sensor Elevation = +31.0 (Alarm EL. 61)",
    "Water Level elevation (B) Sensor Elevation = -28.5 (Alarm EL. 59)",
    "Water Level elevation (C) Sensor Elevation = -67.5 (Alarm EL. 59)",
    "Water Level elevation (D) Sensor Elevation = -80.0 (Alarm EL. 61)",
  ],
  "VWP-WP-08": [
    "Water Level elevation (A) Sensor Elevation = +31.0 (Alarm EL. 61)",
    "Water Level elevation (B) Sensor Elevation = -28.5 (Alarm EL. 59)",
    "Water Level elevation (C) Sensor Elevation = -63.0 (Alarm EL. 59)",
    "Water Level elevation (D) Sensor Elevation = -80.0 (Alarm EL. 61)",
  ],
  "VWP-WP-09": [
    "Water Level elevation (A) Sensor Elevation = +31.0 (Alarm EL. 61)",
    "Water Level elevation (B) Sensor Elevation = -28.5 (Alarm EL. 59)",
    "Water Level elevation (C) Sensor Elevation = -63.0 (Alarm EL. 59)",
    "Water Level elevation (D) Sensor Elevation = -80.0 (Alarm EL. 61)",
  ],
  "VWP-WP-10": [
    "Water Level elevation (A) Sensor Elevation = +31.0 (Alarm EL. 61)",
    "Water Level elevation (B) Sensor Elevation = -28.5 (Alarm EL. 59)",
    "Water Level elevation (C) Sensor Elevation = -63.0 (Alarm EL. 59)",
    "Water Level elevation (D) Sensor Elevation = -80.0 (Alarm EL. 61)",
  ],
  "VWP-WP-11": [
    "Water Level elevation (A) Sensor Elevation = +31.0 (Alarm EL. 61)",
    "Water Level elevation (B) Sensor Elevation = -28.5 (Alarm EL. 59)",
    "Water Level elevation (C) Sensor Elevation = -63.0 (Alarm EL. 59)",
    "Water Level elevation (D) Sensor Elevation = -77.0 (Alarm EL. 61)",
  ],
  "VWP-WP-12": [
    "Water Level elevation (A) Sensor Elevation = +31.0 (Alarm EL. 61)",
    "Water Level elevation (B) Sensor Elevation = -28.5 (Alarm EL. 59)",
    "Water Level elevation (C) Sensor Elevation = -63.0 (Alarm EL. 59)",
    "Water Level elevation (D) Sensor Elevation = -77.0 (Alarm EL. 61)",
  ],
  "VWP-WP-13": [
    "Water Level elevation (A) Sensor Elevation = +31.0 (Alarm EL. 61)",
    "Water Level elevation (B) Sensor Elevation = -28.5 (Alarm EL. 59)",
    "Water Level elevation (C) Sensor Elevation = -63.0 (Alarm EL. 59)",
    "Water Level elevation (D) Sensor Elevation = -80.0 (Alarm EL. 61)",
  ],
   "VWP-WP-14": [
    "Water Level elevation (A) Sensor Elevation = +31.0 (Alarm EL. 61)",
    "Water Level elevation (B) Sensor Elevation = -28.5 (Alarm EL. 59)",
    "Water Level elevation (C) Sensor Elevation = -63.0 (Alarm EL. 59)",
    "Water Level elevation (D) Sensor Elevation = -80.0 (Alarm EL. 61)",
  ],
  "VWP-WP-23": [
    "Water Level elevation (A) Sensor Elevation = +26.0 (Alarm EL. 61)",
    "Water Level elevation (B) Sensor Elevation = -31.0 (Alarm EL. 59)",
    "Water Level elevation (C) Sensor Elevation = -68.0 (Alarm EL. 59)",
    "Water Level elevation (D) Sensor Elevation = -75.0 (Alarm EL. 61)",
  ],
  "VWP-WP-25": [
    "Water Level elevation (A) Sensor Elevation = +31.0 (Alarm EL. 61)",
    "Water Level elevation (B) Sensor Elevation = -28.5 (Alarm EL. 59)",
    "Water Level elevation (B1) Sensor Elevation = -17.0 (Alarm EL. 59)",
    
  ],
  "VWP-WP-26": [
    "Water Level elevation (A) Sensor Elevation = +31.5",
    "Water Level elevation (B1) Sensor Elevation = -17.0",
  ],
  "VWP-WP-27": [
    "Water Level elevation (A) Sensor Elevation = +31.5",
    "Water Level elevation (B1) Sensor Elevation = -17.0",
  ],
  "VWP-WP-28": [
    "Water Level elevation (A) Sensor Elevation = +29.5",
    "Water Level elevation (B1) Sensor Elevation = -17.0",
  ],
  "VWP-WP-29": [
    "Water Level elevation (A) Sensor Elevation = +35.0",
    "Water Level elevation (B1) Sensor Elevation = -20.0",
  ],
  "VWP-WP-30": [
    "Water Level elevation (A) Sensor Elevation = +31.5",
    "Water Level elevation (B1) Sensor Elevation = -21.7",
  ],
  "VWP-WP-31": [
    "Water Level elevation (A) Sensor Elevation = +31.0 (Alarm EL. 61)",
    "Water Level elevation (B) Sensor Elevation = -28.5 (Alarm EL. 59)",
    "Water Level elevation (C) Sensor Elevation = -63.0 (Alarm EL. 59)",
    "Water Level elevation (D) Sensor Elevation = -80.0 (Alarm EL. 61)",
  ],
};


    const chartDiv = chartDivFor(deviceId);
    if (withGraph && chartDiv) {
      const {
        arrayBuffer,
        width: capturedWidth,
        height: capturedHeight,
      } = await captureChartElement(chartDiv);

      const maxWidth = 600;
      const ratio = maxWidth / capturedWidth;
      const newHeight = capturedHeight * ratio;

const allowedDevices = [
  "VWP-WP-01", "VWP-WP-02", "VWP-WP-03", "VWP-WP-04",
  "VWP-WP-05", "VWP-WP-06", "VWP-WP-07",
  "VWP-WP-08", "VWP-WP-09", "VWP-WP-10", "VWP-WP-11",
  "VWP-WP-12", "VWP-WP-13", "VWP-WP-14",
  "VWP-WP-23", "VWP-WP-25", "VWP-WP-26", "VWP-WP-27",
  "VWP-WP-28", "VWP-WP-29", "VWP-WP-30", "VWP-WP-31"
];

const deviceParagraphs = [];

if (allowedDevices.includes(row.device_name)) {

  // "Device: <naam>" page ke upar pehle se chhapta hai (graph se theek pehle),
  // isliye yahan dobara nahi — sirf water-level lines aati hain.

  (waterLevelMap[row.device_name] || []).forEach((text) => {
    deviceParagraphs.push(
      new Paragraph({
        spacing: { before: 100 },
        children: [new TextRun({ text })],
      })
    );
  });
}


      // Multi-sensor hissa jahan se shuru hota hai, us page par sabse upar
      // center me section heading. Sirf pehli baar — aage ke sensor pages par
      // dobara nahi aati.
      const showMultiSensorHeading = isMultiSensor(row) && !multiSensorHeadingDone;
      if (showMultiSensorHeading) multiSensorHeadingDone = true;

      const showInclinometerHeading =
        isInclinometer(row) && !isRebaseDevice(row) && !inclinometerHeadingDone;
      if (showInclinometerHeading) inclinometerHeadingDone = true;

      // Multi-sensor graphs VWP-WP-01 ke page se shuru hote hain, isliye wahan
      // bhi wahi section heading. Device naam ke hisaab se sort hone ki wajah
      // se pehla VWP-WP-* device VWP-WP-01 hi hota hai — hardcode karne ke
      // bajaye "pehla" pakadte hain, taaki 01 export me na aaye to heading
      // section ke saath hi rahe.
      const showVwpHeading =
        !isMultiSensor(row) &&
        /^VWP-WP-/i.test(displayDeviceName(row.device_name)) &&
        !vwpHeadingDone;
      if (showVwpHeading) vwpHeadingDone = true;

      sections.push({
      properties: {
    type: SectionType.NEXT_PAGE,
    page: {
      margin: defaultMargins,
    },
  },
        headers: { default: sharedHeader },
        footers: { default: sharedFooter1 },
        children: [
          ...(showInclinometerHeading
            ? [new Paragraph({
                alignment: AlignmentType.CENTER,
                spacing: { before: 0, after: 300 },
                children: [
                  new TextRun({
                    text: "Inclinometer Reading Graphs",
                    bold: true,
                    size: 20, // half-points = 10pt
                  }),
                ],
              })]
            : []),
          ...(showMultiSensorHeading || showVwpHeading
            ? [new Paragraph({
                alignment: AlignmentType.CENTER,
                spacing: { before: 0, after: 300 },
                children: [
                  new TextRun({
                    text: "Vibrating-Wire Piezometer Readings Multi-Sensor Graphs",
                    bold: true,
                    size: 20, // half-points = 10pt
                  }),
                ],
              })]
            : []),
          new Paragraph({
            spacing: { before: 200, after: 200 },
            children: isMultiSensor(row)
              // Multi-sensor page par "Device: SensorB1" ki jagah
              // "Multi-Sensor Graphs Sensor B1".
              ? [new TextRun({ text: multiSensorLabel(row.device_name), bold: true })]
              : [new TextRun({ text: "Device: " }), new TextRun({ text: displayDeviceName(row.device_name), bold: true })],
          }),
          new Paragraph({
            spacing: { after: 300 },
            children: [
              new ImageRun({
                type: "png",
                data: arrayBuffer,
                transformation: { width: maxWidth, height: newHeight },
              }),
            ],
          }),
 
 ...deviceParagraphs, 
          // pageBreak,
        ],
      });

      
    }



 if (!row.device_profile.startsWith("Sensor"))
 {
   if (withTable) {
      const table = new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows,
    });
 
      sections.push({
           properties: {
    type: SectionType.NEXT_PAGE,
    page: {
      margin: defaultMargins,
    },
  },
      headers: { default: sharedHeader },
    footers: { default: sharedFooter1 },
        children: [
          table,
        ],
      });
    }
 }
 
 
 index = index + 1;
 abortIfCancelled();
 report(10 + Math.round((index / totalDevices) * 70), `Rendering charts (${index}/${totalDevices})`);
  }

sections.push({
  properties: {
    type: SectionType.NEXT_PAGE,
    page: {
      margin: defaultMargins,
    },
  },
  headers: { default: sharedHeader },
    footers: { default: createFooter() },
  children: [
    new Paragraph({
      children: [
        new TextRun("Instruments on Alert from "),
        new TextRun(`${startMonth} `),

        // Start date
        new TextRun(startParts.number),
        new TextRun({ text: startParts.suffix, superscript: true }),

        new TextRun(" to "),

        // Month repeat sirf agar different hai
        ...(startMonth !== endMonth ? [new TextRun(`${endMonth} `)] : []),

        // End date
        new TextRun(endParts.number),
        new TextRun({ text: endParts.suffix, superscript: true }),

        new TextRun(`, ${endDate.getFullYear()}.`),
      ],
    }),
  ],
});

sections.push({
  properties: {
    type: SectionType.NEXT_PAGE,
    page: {
      margin: defaultMargins,
          // verticalAlign: VerticalAlign.CENTER,
    },
      verticalAlign: VerticalAlign.CENTER,

  },
  headers: { default: sharedHeader },
    footers: { default: createFooter() },
  children: [
    new Paragraph({
      spacing: { before: 300, after: 100 },
      alignment: AlignmentType.CENTER,
      children: [
        new TextRun({ text: "Appendix A", bold: true, size: 28 }),
      ],
    }),
  ],
});


        sections.push({
      properties: {
    type: SectionType.NEXT_PAGE,
    page: {
      margin: defaultMargins,
    },
        verticalAlign: VerticalAlign.CENTER,
  },
        headers: { default: sharedHeader },
         footers: { default: createFooter() },
        children: [
         new Paragraph({
      spacing: { 
        before: 300,   
        after: 100, 
       },
      alignment: AlignmentType.CENTER,
      children: [
        new ImageRun({
          type: "png",
          data: imageData12,
          transformation: {
            width: 680,
            height:340
          
          },
        }),
      ],
    }),

        new Paragraph({
      spacing: { 
        before: 300,   
        after: 100, 
       },
      alignment: AlignmentType.CENTER,
      children: [
        new ImageRun({
          type: "png",
          data: imageData13,
          transformation: {
            width: 680,
            height:340
          
          },
        }),
      ],
    }),
          // pageBreak,
        ],
      });

 
const data = [
  ["INC-WP-29", "22", "113.71"],
  ["INC-WP-30", "22", "112.92"],
  ["INC-WP-31", "22", "114.46"],
  ["INC-WP-32", "22", "112.67"],
  ["INC-WP-33", "22", "114.75"],
  ["INC-WP-34", "22", "114.12"],
  ["INC-WP-37", "29", "148.25"],
  ["INC-WP-38", "30", "152.17"],
  ["INC-WP-39", "30", "151.75"],
  ["INC-WP-40", "30", "153"],
  ["INC-WP-41", "30", "152"],
  ["INC-WP-42", "25", "127.5"],
  ["INC-WP-45", "12", "61.75"],
  ["INC-WP-46", "29", "147.5"],
  ["INC-WP-47", "29", "146.75"],
  ["INC-WP-48", "30", "151.75"],
  ["INC-WP-49", "30", "152.17"],
  ["INC-WP-15", "27", "137.25"],
  ["INC-WP-16", "27", "137.17"],
  ["INC-WP-17", "27", "136.29"],
  ["INC-WP-18", "27", "136.22"],
  ["INC-WP-19", "26", "135.13"],
  ["INC-WP-20", "27", "135.83"],
  ["INC-WP-21", "27", "137.50"],
  ["INC-WP-22", "27", "137.75"],
  ["INC-WP-23", "27", "137.17"],
  ["INC-WP-24", "27", "137.63"],
  ["INC-WP-25", "27", "136.67"],
  ["INC-WP-26", "27", "137.25"],
  ["INC-WP-01", "26", "133.67"],
  ["INC-WP-14", "26", "133"],
  ["INC-WP-03", "26", "133.5"],
  ["INC-WP-04", "26", "133.75"],
  ["INC-WP-06", "26", "133.5"],
  ["INC-WP-07", "26", "133.42"],
  ["INC-WP-08", "26", "133.38"],
  ["INC-WP-11", "26", "133.42"],
  ["INC-WP-09", "26", "134.25"],
  ["INC-WP-12", "26", "133.50"],
  ["INC-WP-27", "26", "138.08"],
  ["INC-WP-28", "27", "136.21"],
];

const makeCell = (text, isHeader = false) =>
  new TableCell({
    children: [
      new Paragraph({
        alignment: AlignmentType.CENTER,
        children: [
          new TextRun({
            text,
            bold: isHeader,
          }),
        ],
      }),
    ],
    shading: isHeader
      ? { type: ShadingType.CLEAR} 
      : undefined,
  });

// Header row
const headerRow = new TableRow({
  children: [
    makeCell("Instrument ID", true),
    makeCell("Total Number of Sensors", true),
    makeCell("Total Depth (ft)", true),
  ],
});

// Data rows
const tableRows = data.map(
  (row) =>
    new TableRow({
      children: row.map((cellText) => makeCell(cellText)),
    })
);

sections.push({
  properties: {
    type: SectionType.NEXT_PAGE,
    page: { margin: defaultMargins },
  },
  headers: { default: sharedHeader },
     footers: { default: createFooter() },
  children: [
    new Paragraph({
      spacing: { after: 400 },
      children: [new TextRun({ text: "Inclinometer Details"})],
    }),
    new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: [headerRow, ...tableRows],  
    }),
  ],
});



const piezometerData = [
  ["VWP-WP-01", "A", "35.5", "CAT", "57.07", "EL = 61 *", "EL = 63", "EL = 65", "Silty Clay W/ Gravel"],
  ["VWP-WP-01", "B1", "83.5", "CAT", "57.99", "EL = 59 *", "EL = 61", "EL = 63", "Sand Gravel AND Silty Clay"],
  ["VWP-WP-01", "B", "95", "CAT", "59.42", "EL = 59 *", "EL = 61", "EL = 63", "Silty Clay W/ Gravel AND Sand"],
  ["VWP-WP-01", "C", "129.5", "CAT", "62.42", "EL = 59 *", "EL = 61", "EL = 63", "Silty Clay"],
  ["VWP-WP-01", "D", "146.5", "CAT", "68.21", "EL = 61 *", "EL = 63", "EL = 65", "CLAY W/ Silty Sand"],

  ["VWP-WP-02", "A", "35.5", "CAT", "56.79", "EL = 61 *", "EL = 63", "EL = 65", "Very Silty Clay"],
  ["VWP-WP-02", "B", "95", "CAT", "57.46", "EL = 59 *", "EL = 61", "EL = 63", " Very Clayey Silt"],
  ["VWP-WP-02", "C", "129.5", "CAT", "63.15", "EL = 59 *", "EL = 61", "EL = 63", "Silty Sand clay with Gravel"],
  ["VWP-WP-02", "D", "143.5", "CAT", "67.12", "EL = 61 *", "EL = 63", "EL = 65", "Sandy very Clayey Silt"],

  ["VWP-WP-03", "A", "35.5", "C&C", "56.17", "EL = 61 *", "EL = 63", "EL = 65", "No Sample"],
  ["VWP-WP-03", "B1", "83.5", "C&C", "57.71", "EL = 59 *", "EL = 61", "EL = 63", "Silty Clay"],
  ["VWP-WP-03", "B", "95", "C&C", "58.45", "EL = 59 *", "EL = 61", "EL = 63", "Clayey Silty Sand"],
  ["VWP-WP-03", "C", "129.5", "C&C", "60.57", "EL = 59 *", "EL = 61", "EL = 63", "FINE Silty Sand"],
  ["VWP-WP-03", "D", "146.5", "C&C", "73.25", "EL = 61 *", "EL = 63", "EL = 65", "Clayey Silt"],

  ["VWP-WP-04", "A", "35.5", "C&C/U-WALL", "55.3", "EL = 61 *", "EL = 63", "EL = 65", ""],
  ["VWP-WP-04", "B1", "83.5", "C&C/U-WALL", "58.19", "EL = 59 *", "EL = 61", "EL = 63", "Sand W/Gravel"],
  ["VWP-WP-04", "B", "95", "C&C/U-WALL", "58", "EL = 59 *", "EL = 61", "EL = 63", "Sand W/Gravel"],
  ["VWP-WP-04", "C", "129.5", "C&C/U-WALL", "62.63", "EL = 59 *", "EL = 61", "EL = 63", "Clay"],

  ["VWP-WP-05", "A", "35.5", "U-WALL", "55.28", "EL = 61 *", "EL = 63", "EL = 65", "Silty Sand w/Gravel"],
  ["VWP-WP-05", "B1", "83.5", "U-WALL", "56.15", "EL = 59 *", "EL = 61", "EL = 63", "Clayey Silty"],
  ["VWP-WP-05", "B", "95", "U-WALL", "57.69", "EL = 59*", "EL = 61", "EL = 63", " Clay"],
  ["VWP-WP-05", "C", "129.5", "U-WALL", "66.59", "EL = 59 *", "EL = 61", "EL = 63", "Silty Sand"],

  ["VWP-WP-06", "A", "35.5", "C&C", "56.92", "EL = 61 *", "EL = 63", "EL = 65", "Very Silty"],
  ["VWP-WP-06", "B", "95", "C&C", "57.17", "EL = 59 *", "EL = 61", "EL = 63", "Sand with Gravel"],
  ["VWP-WP-06", "C", "129.5", "C&C", "68.8", "EL = 59 *", "EL = 61", "EL = 63", "Clay"],
  ["VWP-WP-06", "D", "143.5", "C&C", "69.19", "EL = 61 *", "EL = 63", "EL = 65", "Sand"],

  ["VWP-WP-07", "A", "35.5", "C&C", "58.75", "EL = 61 *", "EL = 63", "EL = 65", "Sandy Clay"],
  ["VWP-WP-07", "B", "95", "C&C", "61.5", "EL = 59 *", "EL = 61", "EL = 63", "Sandy CLAYEY Gravel"],
  ["VWP-WP-07", "C", "134", "C&C", "72.45", "EL = 59 *", "EL = 61", "EL = 63", "CLAYEY Gravel"],
  ["VWP-WP-07", "D", "146.5", "C&C", "69.63", "EL = 61 *", "EL = 63", "EL = 65", "Sandy Silty Clay"],

  ["VWP-WP-08", "A", "35.5", "U-WALL", "56.17", "EL = 61 *", "EL = 63", "EL = 65", "Silty Sand w/Gravel"],
  ["VWP-WP-08", "B", "95", "U-WALL", "51.79", "EL = 59 *", "EL = 61", "EL = 63", "Clayey Silt"],
  ["VWP-WP-08", "C", "129.5", "U-WALL", "62.39", "EL = 59 *", "EL = 61", "EL = 63", "Sandy Silt"],
  ["VWP-WP-08", "D", "146.5", "U-WALL", "67.36", "EL = 61 *", "EL = 63", "EL = 65", "Silty Clay"],

  ["VWP-WP-09", "A", "35.5", "U-WALL", "56.07", "EL = 61 *", "EL = 63", "EL = 65", "Silty Sand"],
  ["VWP-WP-09", "B", "95", "U-WALL", "58.17", "EL = 59 *", "EL = 61", "EL = 63", "Gravel  w/Sand"],
  ["VWP-WP-09", "C", "129.5", "U-WALL", "64.97", "EL = 59 *", "EL = 61", "EL = 63", "Silty Clay"],
  ["VWP-WP-09", "D", "146.5", "U-WALL", "68.67", "EL = 61 *", "EL = 63", "EL = 65", "Clayey Silt"],

  ["VWP-WP-10", "A", "35.5", "U-WALL", "70.29", "EL = 61 *", "EL = 63", "EL = 65", "Clayey Silt w/Gravel"],
  ["VWP-WP-10", "B", "95", "U-WALL", "55.84", "EL = 59 *", "EL = 61", "EL = 63", "Clayey Silt"],
  ["VWP-WP-10", "C", "129.5", "U-WALL", "64.05", "EL = 59 *", "EL = 61", "EL = 63", "Silty Clay"],
  ["VWP-WP-10", "D", "146.5", "U-WALL", "66.93", "EL = 61 *", "EL = 63", "EL = 65", "Silty Clay"],

  ["VWP-WP-11", "A", "35.5", "CAT", "57.86", "EL = 61 *", "EL = 63", "EL = 65", "Clay"],
  ["VWP-WP-11", "B", "95", "CAT", "57.87", "EL = 59 *", "EL = 61", "EL = 63", "Sand"],
  ["VWP-WP-11", "C", "129.5", "CAT", "63.41", "EL = 59 *", "EL = 61", "EL = 63", "Clay"],
  ["VWP-WP-11", "D", "143.5", "CAT", "68.21", "EL = 61 *", "EL = 63", "EL = 65", "Clayey Silty Sand"],

  ["VWP-WP-12", "A", "35.5", "CAT", "53.1", "EL = 61 *", "EL = 63", "EL = 65", "Silty Clay"],
  ["VWP-WP-12", "B", "95", "CAT", "54.21", "EL = 59 *", "EL = 61", "EL = 63", "Sandy Gravel"],
  ["VWP-WP-12", "C", "129.5", "CAT", "54.21", "EL = 59 *", "EL = 61", "EL = 63", "Clay"],
  ["VWP-WP-12", "D", "143.5", "CAT", "63.76", "EL = 61 *", "EL = 63", "EL = 65", "Sandy Silt"],

  ["VWP-WP-13", "A", "35.5", "CAT", "56.42", "EL = 61 *", "EL = 63", "EL = 65", "Silty Sand to very Clayey Silt"],
  ["VWP-WP-13", "B", "95", "CAT", "56.27", "EL = 59 *", "EL = 61", "EL = 63", "Silty Sand w/coarse Gravel"],
  ["VWP-WP-13", "C", "129.5", "CAT", "59.01", "EL = 59 *", "EL = 61", "EL = 63", "Clayey Silty"],
  ["VWP-WP-13", "D", "146.5", "CAT", "68.44", "EL = 61 *", "EL = 63", "EL = 65", "Sand"],

  ["VWP-WP-14", "A", "35.5", "CAT", "55.64", "EL = 61 *", "EL = 63", "EL = 65", "Silty Clay"],
  ["VWP-WP-14", "B", "95", "CAT", "59.99", "EL = 59 *", "EL = 61", "EL = 63", "Sandy Gravel"],
  ["VWP-WP-14", "C", "129.5", "CAT", "62.8", "EL = 59 *", "EL = 61", "EL = 63", "Silty Clay"],
  ["VWP-WP-14", "D", "146.5", "CAT", "67.85", "EL = 61 *", "EL = 63", "EL = 65", "Clayey Silty Sand"],

  ["VWP-WP-31", "A", "35.5", "C&C", "56.28", "EL = 61 *", "EL = 63", "EL = 65", "Silty Sand to Silty Clay"],
  ["VWP-WP-31", "B", "95", "C&C", "58.81", "EL = 59 *", "EL = 61", "EL = 63", "Sandy Gravel"],
  ["VWP-WP-31", "C", "129.5", "C&C", "62.25", "EL = 59 *", "EL = 61", "EL = 63", "Silty Clay"],
  ["VWP-WP-31", "D", "146.5", "C&C", "71.39", "EL = 61 *", "EL = 63", "EL = 65", "Silty CLay"],

  ["VWP-WP-23", "A", "41.5", "Regional", "", "EL = 61 *", "EL = 63", "EL = 65", "Clayey Sand w/Gravel"],
  ["VWP-WP-23", "B", "97.5", "Regional", "", "EL = 59 *", "EL = 61", "EL = 63", "Clayey Sandy Silt"],
  ["VWP-WP-23", "C", "134.5", "Regional", "", "EL = 59 *", "EL = 61", "EL = 63", "Clayey Silty Sand"],
  ["VWP-WP-23", "D", "141.5", "Regional", "", "EL = 61 *", "EL = 63", "EL = 65", "Clayey Silty Sand"],

  ["VWP-WP-26", "A", "35.0", "CAT", "50.84", "", "", "", "Silty Sandy, clayey"],
  ["VWP-WP-26", "B1", "83.0", "CAT", "47.96", "", "", "", "Silty clayey, silty sand"],

  ["VWP-WP-27", "A", "35.0", "CAT", "54.08", "", "", "", "silty sandy Clay"],
  ["VWP-WP-27", "B1", "83.5", "CAT", "46.51", "", "", "", "Clayey Sandy Gravel"],

  ["VWP-WP-28", "A", "37.0", "CAT", "60.2", "", "", "", "Sandy clayey"],
  ["VWP-WP-28", "B1", "83.5", "CAT", "35.62", "", "", "", "Silty Clay"],

  ["VWP-WP-29", "A", "31.5", "CAT", "62.94", "", "", "", "Sandy gravel"],
  ["VWP-WP-29", "B1", "86.5", "CAT", "47.27", "", "", "", "Silt"],

  ["VWP-WP-30", "A", "35.0", "CAT", "53.08", "", "", "", "Sandy Gravel"],
  ["VWP-WP-30", "B1", "88.2", "CAT", "48.69", "", "", "", "Silty Sand"],

  // Sensor A field se hata diya gaya — graph/table se bhi filter hota hai
  // (utils/hiddenSensors.js), isliye yahan appendix me bhi nahi aana chahiye.
  ["VWP-WP-25", "B1", "83", "CAT", "57.57", "", "", "", "Silty Clay"],
  ["VWP-WP-25", "B", "95", "CAT", "55.16", "", "", "", "Silty Clay"],
];



const makeCell1 = (text, isHeader = false) =>
  new TableCell({
    children: [
      new Paragraph({
        alignment: AlignmentType.CENTER,
        children: [new TextRun({ text, bold: isHeader })],
      }),
    ],
    shading: isHeader
      ? { type: ShadingType.CLEAR, color: "auto", fill: "D9D9D9" }
      : undefined,
  });


const headerRow1 = new TableRow({
  children: [
    makeCell("VWP ID", true),
    makeCell("VWP Sensor ID", true),
    makeCell("VWP Sensor Depth (ft)", true),
    makeCell("Location on Site", true),
    makeCell("Baseline Elevation", true),
    makeCell("Alarm Level During Depressurization", true),
    makeCell("Action Level During Depressurization", true),
    makeCell("Maximum Level During Depressurization", true),
    makeCell("Soil Classification", true),
  ],
});

const dataRows1 = piezometerData.map(
  (row) =>
    new TableRow({
      children: row.map((cellText) => makeCell1(cellText)),
    })
);



sections.push({
  properties: {
    type: SectionType.NEXT_PAGE,
    page: { margin: defaultMargins },
  },
  headers: { default: sharedHeader },
    footers: { default: createFooter() },
  children: [
    new Paragraph({
      spacing: { after: 400 },
      children: [new TextRun({ text: "Piezometer Details", })],
    }),
    new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: [headerRow1, ...dataRows1],
    }),
  ],
});


      
 
 
  abortIfCancelled();
  report(85, "Assembling document");

  // Times New Roman is the document-wide default. Runs that set `font`
  // explicitly (the Monotype Corsiva letterhead in the header/footer) still
  // win, so the branding is untouched.
  const doc = new Document({
    styles: {
      default: {
        document: {
          run: { font: "Times New Roman" },
        },
      },
    },
    sections,
  });

  report(95, "Packaging file");
  const blob = await Packer.toBlob(doc);

  // Last chance to bail before anything reaches the user's disk.
  abortIfCancelled();
  report(100, "Complete");
  saveAs(blob, `report-${Date.now()}.docx`);
};
